/**
 * Registro dos módulos de atendimento.
 *
 * Um tipo de atendimento (CompanyConfig.AppointmentTypes) é uma lista ordenada de
 * chaves deste registro. As chaves são fixas em código e espelham
 * `AppointmentTypes::MODULE_KEYS` no backend. Aqui ficam só os metadados: quem
 * renderiza cada módulo é `components/appointmentModules/AppointmentModules.js`,
 * para que a tela de configuração possa importar este arquivo sem puxar os cards.
 *
 * Para criar um módulo novo: adicionar a linha aqui (com `details`), a chave no backend, o card em
 * AppointmentModules, a maquete em popup/CreateEditAppointmentType/ModulePreview.js e a
 * consulta no get_appointment_info (ver checklist em
 * docs/features/modular-appointments/analise-e-proposta.md).
 *
 * Escalas clínicas são a exceção: cada escala vira um módulo a partir do
 * catálogo em ./scales (ver docs/features/scale-modules/README.md).
 */

import { SCALES } from './scales';

// Categoria de cada módulo: só serve de filtro na tela de configuração dos tipos
export const MODULE_CATEGORY = Object.freeze({
  GENERAL: 'general',
  CLINICAL: 'clinical',
  PEDIATRICS: 'pediatrics',
  BODY: 'body',
  SCALES: 'scales',
});

export const MODULE_CATEGORIES = Object.freeze([
  { key: MODULE_CATEGORY.GENERAL, label: 'Geral' },
  { key: MODULE_CATEGORY.CLINICAL, label: 'Prontuário' },
  { key: MODULE_CATEGORY.PEDIATRICS, label: 'Pediatria' },
  { key: MODULE_CATEGORY.BODY, label: 'Nutrição e estética' },
  { key: MODULE_CATEGORY.SCALES, label: 'Escalas clínicas' },
]);

export function getCategoryLabel(key) {
  const category = MODULE_CATEGORIES.find((item) => item.key === key);
  return category ? category.label : key;
}

export const MODULE_LIFECYCLE = Object.freeze({
  // Registro próprio de cada consulta (evolução, anotações, documentos)
  PER_APPOINTMENT: 'per_appointment',
  // Dado vigente do paciente; cada alteração gera um snapshot (queixas, anamnese)
  PATIENT_CURRENT: 'patient_current',
  // Uma medição por atendimento lida como série histórica (assimetria craniana)
  SERIES: 'series',
});

export const MODULE_KEYS = Object.freeze({
  NOTES: 'notes',
  COMPLAINTS: 'complaints',
  EVOLUTION: 'evolution',
  ANAMNESIS: 'anamnesis',
  CRANIAL_ASYMMETRY: 'cranial_asymmetry',
  GROWTH_CURVE: 'growth_curve',
  MOTOR_MILESTONES: 'motor_milestones',
  DIAGNOSES: 'diagnoses',
  DOCUMENTS: 'documents',
  ATTACHMENTS: 'attachments',
  BODY_MEASUREMENTS: 'body_measurements',
  EVOLUTION_PHOTOS: 'evolution_photos',
  BODY_MAP: 'body_map',
  MEAL_PLAN: 'meal_plan',
  APPLICATION_MAP: 'application_map',
  THERMOGRAPHY: 'thermography',
  AIMS: 'aims',
});

const asList = (list) => (Array.isArray(list) ? list : []);

export function parseComplaintsList(data) {
  try {
    const raw = data?.complaints?.[0]?.list;
    if (raw && typeof raw === 'string') {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    // lista corrompida: trata como vazia
  }
  return [];
}

/** Anotação gravada para este atendimento (no máximo uma por consulta). */
export function findAppointmentNote(list, appointmentId) {
  if (!Array.isArray(list) || appointmentId == null) return null;
  return list.find((note) => String(note.appointmentId) === String(appointmentId)) || null;
}

export const APPOINTMENT_MODULES = Object.freeze([
  {
    key: MODULE_KEYS.NOTES,
    label: 'Anotações',
    description: 'Um campo livre de anotações do atendimento, com formatação',
    details: 'Um campo de texto livre por consulta, com negrito, itálico, sublinhado e listas numeradas ou com marcadores. As anotações das consultas anteriores ficam listadas abaixo do campo. Nunca é obrigatório: ao finalizar, grava o que houver.',
    lifecycle: MODULE_LIFECYCLE.PER_APPOINTMENT,
    category: MODULE_CATEGORY.GENERAL,
    records: (data) => asList(data?.notes),
  },
  {
    key: MODULE_KEYS.COMPLAINTS,
    label: 'Queixas principais',
    description: 'Lista de queixas vigentes do paciente',
    details: 'Etiquetas curtas com as queixas atuais do paciente, no topo da tela. A lista é do paciente, não da consulta: o que for alterado aqui vale para os próximos atendimentos.',
    lifecycle: MODULE_LIFECYCLE.PATIENT_CURRENT,
    category: MODULE_CATEGORY.CLINICAL,
    // Lista vigente do paciente: conta como um registro, o último snapshot
    records: (data) => (parseComplaintsList(data).length > 0 ? data.complaints.slice(0, 1) : []),
  },
  {
    key: MODULE_KEYS.EVOLUTION,
    label: 'Queixa e evolução',
    description: 'Registro clínico da consulta e histórico de evoluções',
    details: 'Dois campos por consulta, Queixa e Evolução, com modelos de texto e rascunho automático. O histórico das consultas anteriores aparece em um card logo abaixo. Com este módulo, a queixa passa a ser obrigatória para finalizar.',
    lifecycle: MODULE_LIFECYCLE.PER_APPOINTMENT,
    category: MODULE_CATEGORY.CLINICAL,
    records: (data) => asList(data?.history),
  },
  {
    key: MODULE_KEYS.ANAMNESIS,
    label: 'Anamnese',
    description: 'Formulários de anamnese do paciente',
    details: 'Preenchimento e revisão dos modelos de anamnese cadastrados em Modelos > Anamneses. Com um modelo fixo no tipo, o card já abre o botão para preencher esse modelo; sem modelo fixo, o profissional escolhe na hora.',
    lifecycle: MODULE_LIFECYCLE.PATIENT_CURRENT,
    category: MODULE_CATEGORY.CLINICAL,
    records: (data) => asList(data?.anamneses),
  },
  {
    key: MODULE_KEYS.CRANIAL_ASYMMETRY,
    label: 'Assimetria craniana',
    description: 'Medições cranianas com evolução e classificação',
    details: 'Uma medição por consulta (AP, BP, diagonais) lida como série: tabela com a evolução, classificação automática nas escalas, fotos por medição e relatório em PDF para compartilhar com a família.',
    lifecycle: MODULE_LIFECYCLE.SERIES,
    category: MODULE_CATEGORY.PEDIATRICS,
    records: (data) => asList(data?.cranialAsymetry),
  },
  {
    key: MODULE_KEYS.GROWTH_CURVE,
    label: 'Curva de crescimento',
    description: 'Peso, comprimento/altura e PC nas curvas da OMS',
    details: 'Medições datadas de peso, comprimento/altura e perímetro cefálico sobre as curvas da OMS (peso, comprimento/altura, PC e IMC por idade), com percentil e escore-z de cada medição, histórico e relatório em PDF.',
    lifecycle: MODULE_LIFECYCLE.SERIES,
    category: MODULE_CATEGORY.PEDIATRICS,
    records: (data) => asList(data?.growthMeasurements),
  },
  {
    key: MODULE_KEYS.MOTOR_MILESTONES,
    label: 'Marcos motores',
    description: 'Conquistas motoras do bebê nas janelas da OMS',
    details: 'Data em que o bebê sentou, engatinhou, ficou em pé e andou, comparada com as janelas de conquista da OMS, com idade corrigida para prematuros, situação de cada marco, gráfico das janelas e relatório em PDF.',
    lifecycle: MODULE_LIFECYCLE.PATIENT_CURRENT,
    category: MODULE_CATEGORY.PEDIATRICS,
    records: (data) => asList(data?.motorMilestones),
  },
  {
    key: MODULE_KEYS.AIMS,
    label: 'Escala motora de Alberta (AIMS)',
    description: 'Escore por postura, total e percentil, de 0 a 18 meses',
    details: 'Para fisioterapia e pediatria: o profissional aplica a escala de Alberta com o material oficial e registra o escore de cada postura (prono, supino, sentado e em pé). O card soma o total, mostra a idade na avaliação (corrigida para prematuros) e, com o percentil lido no gráfico oficial, a faixa de interpretação. Histórico em tabela, leitura na aba Saúde e relatório em PDF. Os itens e as curvas da escala não são reproduzidos.',
    lifecycle: MODULE_LIFECYCLE.SERIES,
    category: MODULE_CATEGORY.PEDIATRICS,
    records: (data) => asList(data?.aimsAssessments),
  },
  {
    key: MODULE_KEYS.DIAGNOSES,
    label: 'Diagnósticos (CID)',
    description: 'Diagnósticos da consulta pela CID-10 ou pela CID-11',
    details: 'Busca na CID-10 (a usada hoje em atestados, convênios e no SUS) ou na CID-11 da OMS, pelo código ou por parte do nome, sem diferenciar acentos. Cada diagnóstico escolhido fica gravado no atendimento com a classificação, o código, o título e a versão; os das consultas anteriores aparecem logo abaixo. Entra na aba Saúde e no relatório completo.',
    lifecycle: MODULE_LIFECYCLE.PER_APPOINTMENT,
    category: MODULE_CATEGORY.CLINICAL,
    records: (data) => asList(data?.diagnoses),
  },
  {
    key: MODULE_KEYS.DOCUMENTS,
    label: 'Documentos',
    description: 'Atestados, receitas, recibos e outros documentos emitidos',
    details: 'Emissão de atestados, receitas, recibos e outros documentos a partir dos modelos da clínica, com os dados do paciente já preenchidos. A receita pode ser simples ou de controle especial, em duas vias. Os documentos emitidos ficam listados no card para baixar de novo.',
    lifecycle: MODULE_LIFECYCLE.PER_APPOINTMENT,
    category: MODULE_CATEGORY.GENERAL,
    records: (data) => [...asList(data?.documents), ...asList(data?.prescriptions)],
  },
  {
    key: MODULE_KEYS.ATTACHMENTS,
    label: 'Anexos',
    description: 'Fotos e arquivos enviados do paciente',
    details: 'Envio de fotos, exames e outros arquivos do paciente, organizados por categoria, com miniaturas e visualização. Os arquivos ficam na ficha do paciente e aparecem em qualquer consulta.',
    lifecycle: MODULE_LIFECYCLE.PER_APPOINTMENT,
    category: MODULE_CATEGORY.GENERAL,
    // Só os anexos gerais: fotos de evolução, craniana e do mapa são de outros módulos
    records: (data) => asList(data?.patientAttachments).filter((item) => item && (item.context || 'general') === 'general'),
  },
  {
    key: MODULE_KEYS.BODY_MEASUREMENTS,
    label: 'Medidas corporais',
    description: 'Peso, altura, circunferências e dobras com evolução',
    details: 'Uma avaliação por consulta com peso, altura, percentual de gordura, circunferências, dobras cutâneas e diâmetros ósseos, mostradas sobre uma silhueta com a variação em relação à avaliação anterior. Calcula o IMC classificado, a gordura pelas dobras (Jackson-Pollock ou Durnin-Womersley) com massa gorda e magra e as relações cintura-quadril e cintura-estatura. Nenhuma medida é obrigatória: registra-se o que foi medido. Tabela com todas as avaliações para consulta precisa.',
    lifecycle: MODULE_LIFECYCLE.SERIES,
    category: MODULE_CATEGORY.BODY,
    records: (data) => asList(data?.bodyMeasurements),
  },
  {
    key: MODULE_KEYS.EVOLUTION_PHOTOS,
    label: 'Fotos de evolução',
    description: 'Fotos do paciente por região e ângulo, com comparação',
    details: 'Fotos enviadas no atendimento (uma ou várias, direto da câmera do celular) identificadas por região do corpo, ângulo e data. A galeria agrupa por sessão; duas fotos selecionadas abrem o comparador lado a lado ou com controle deslizante, e a linha do tempo troca a data comparada. As fotos ficam na ficha do paciente, junto dos outros arquivos.',
    lifecycle: MODULE_LIFECYCLE.SERIES,
    category: MODULE_CATEGORY.BODY,
    records: (data) => asList(data?.patientAttachments).filter((item) => item && item.context === 'evolution'),
  },
  {
    key: MODULE_KEYS.BODY_MAP,
    label: 'Mapa corporal',
    description: 'Pontos de queixa e observação marcados na silhueta',
    details: 'Um clique na silhueta (frente ou costas) cria um marcador numerado com a região sugerida, o tipo (queixa, observação ou área avaliada), a intensidade e uma nota curta; também dá para marcar pela lista de regiões. O mapa é da consulta e é salvo a cada marcador; os mapas anteriores podem ser sobrepostos para comparar.',
    lifecycle: MODULE_LIFECYCLE.PER_APPOINTMENT,
    category: MODULE_CATEGORY.BODY,
    records: (data) => asList(data?.bodyMap),
  },
  {
    key: MODULE_KEYS.MEAL_PLAN,
    label: 'Plano alimentar',
    description: 'Refeições com porções e substituições, entregue em PDF',
    details: 'O plano vigente do paciente aparece como ele o recebe: cada refeição com horário, itens com porção e unidade, substituições marcadas como alternativa e orientações gerais. A edição acontece dentro da estrutura (adicionar, duplicar, reordenar e remover refeições, itens e substituições). Uma nova versão parte do plano atual e guarda o anterior no histórico; o PDF sai com a identificação da clínica, do profissional e do paciente.',
    lifecycle: MODULE_LIFECYCLE.SERIES,
    category: MODULE_CATEGORY.BODY,
    records: (data) => asList(data?.mealPlans),
  },
  {
    key: MODULE_KEYS.APPLICATION_MAP,
    label: 'Mapa de aplicação',
    description: 'Pontos e traços de aplicação com produto e quantidade',
    details: 'Para estética e procedimentos injetáveis: cada clique na figura (rosto, frente ou costas) marca um ponto e cada arrasto desenha um traço com direção, já com o produto ativo e a quantidade (UI, ml, unidades). A lista de produtos é da clínica, com cor e quantidade padrão de cada um. O card soma os totais por produto na sessão, mostra ou esconde as quantidades na figura e sobrepõe os mapas anteriores para comparar. Salvo a cada alteração, com leitura na aba Saúde.',
    lifecycle: MODULE_LIFECYCLE.PER_APPOINTMENT,
    category: MODULE_CATEGORY.BODY,
    records: (data) => asList(data?.applicationMaps),
  },
  {
    key: MODULE_KEYS.THERMOGRAPHY,
    label: 'Termografia',
    description: 'Temperatura da pele por local, com diferença entre os lados',
    details: 'Para fisioterapia, estética e medicina esportiva: a temperatura de cada local do corpo medida na câmera termográfica, escolhida numa lista por região (face, tronco, membros) com lado direito e esquerdo, mais locais livres. O card calcula a diferença entre os lados (delta T), destaca a assimetria acima do limiar de referência e compara com a avaliação anterior. Temperatura da sala e observação opcionais, leitura na aba Saúde e relatório em PDF.',
    lifecycle: MODULE_LIFECYCLE.SERIES,
    category: MODULE_CATEGORY.BODY,
    records: (data) => asList(data?.thermography),
  },
  // Escalas clínicas: um módulo por escala do catálogo, todas na mesma tabela
  ...SCALES.map((scale) => ({
    key: scale.key,
    label: scale.name,
    description: scale.description,
    details: scale.details,
    lifecycle: MODULE_LIFECYCLE.SERIES,
    category: MODULE_CATEGORY.SCALES,
    records: (data) => asList(data?.scaleAssessments).filter((record) => record && record.scaleKey === scale.key),
  })),
]);

export function getModuleByKey(key) {
  return APPOINTMENT_MODULES.find((module) => module.key === key) || null;
}

export function getModuleLabel(key) {
  const module = getModuleByKey(key);
  return module ? module.label : key;
}

function validTypeKeys(typeModules) {
  const keys = [];
  (Array.isArray(typeModules) ? typeModules : []).forEach((key) => {
    if (getModuleByKey(key) && !keys.includes(key)) keys.push(key);
  });
  return keys;
}

/**
 * Todos os módulos com registro do paciente: módulos do tipo (na ordem
 * configurada) mais qualquer módulo com dados, de qualquer consulta. Usada onde
 * não há tipo em vigor (ficha do paciente, atendimento ainda não iniciado).
 */
export function resolveVisibleModules(typeModules, data) {
  const visible = validTypeKeys(typeModules);

  APPOINTMENT_MODULES.forEach((module) => {
    if (!visible.includes(module.key) && module.records(data).length > 0) {
      visible.push(module.key);
    }
  });

  return visible;
}

/**
 * Regra de exibição do atendimento com tipo em vigor. `visible`: módulos do tipo
 * (na ordem configurada) mais os que têm registro feito neste atendimento, para
 * que nada gravado antes de uma troca de tipo fique escondido. `others`: módulos
 * fora do tipo que só têm registros de consultas anteriores; não viram card, só
 * um atalho no fim da tela ([{ key, count }], na ordem do registro).
 */
export function splitModules(typeModules, data, appointmentId) {
  const visible = validTypeKeys(typeModules);
  const others = [];

  APPOINTMENT_MODULES.forEach((module) => {
    if (visible.includes(module.key)) return;
    const records = module.records(data);
    if (records.length === 0) return;
    const fromThisAppointment = appointmentId != null
      && records.some((record) => record && String(record.appointmentId) === String(appointmentId));
    if (fromThisAppointment) {
      visible.push(module.key);
    } else {
      others.push({ key: module.key, count: records.length });
    }
  });

  return { visible, others };
}
